import {
  ClientSearchApiError,
  ClientSearchBillingStatus,
  ClientSearchDetail,
  ClientSearchList,
  ClientSearchOwner,
  ClientSearchPracticeCreated,
  ClientSearchResult,
  ClientSearchRow,
  ClientSearchSentiment,
} from "core/types/client-search.types";

// I JSON delle ricerche vengono da tre anni di versioni diverse di SFM/RM: qui ogni campo
// si normalizza al tipo atteso, cosi' le pagine non devono difendersi da null o numeri
// dove si aspettano stringhe.
const asString = (v: any): string =>
  typeof v === "string" ? v : v === null || v === undefined ? "" : String(v);

const asNumberOrNull = (v: any): number | null => {
  // null/undefined/"" NON passano da Number(): Number(null) vale 0, e uno 0 preso per
  // buono mostrerebbe "0 risultati" o "0%" dove il dato semplicemente manca.
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Distinti, in ordine di inserimento: RM inserisce le stesse parole chiave piu' volte.
// I link NON si ritoccano (trim=false): il backend li confronta come stringhe esatte.
const uniqueStrings = (v: any, trim = true): string[] => {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  v.forEach((item) => {
    const s = trim ? asString(item).trim() : asString(item);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
};

const BILLING_STATUSES: ClientSearchBillingStatus[] = [
  "active",
  "past_due",
  "suspended",
];

// NULL in DB => "active" lo fa gia' il backend; qualunque valore inatteso si tratta allo
// stesso modo, per non mostrare "sospeso" a chi non lo e'.
const asBillingStatus = (v: any): ClientSearchBillingStatus =>
  BILLING_STATUSES.includes(v) ? v : "active";

export const transformClientSearchOwner = (data: any): ClientSearchOwner => ({
  id: asString(data?.id),
  email: asString(data?.email),
  firstName: asString(data?.first_name),
  lastName: asString(data?.last_name),
  billingStatus: asBillingStatus(data?.billing_status),
  level:
    data?.level === null || data?.level === undefined
      ? null
      : asString(data.level),
});

export const transformClientSearchRow = (data: any): ClientSearchRow => ({
  searchId: Number(data?.search_id),
  searchCreationTime: asString(data?.search_creation_time),
  fullName: asString(data?.full_name),
  params: uniqueStrings(data?.params),
  totResults: asNumberOrNull(data?.tot_results),
  owner: transformClientSearchOwner(data?.owner),
});

export const transformClientSearchList = (data: any): ClientSearchList => ({
  searches: (Array.isArray(data?.searches) ? data.searches : []).map(
    (d: any) => transformClientSearchRow(d)
  ),
  // Solo un false esplicito vale come "anagrafica non leggibile".
  ownersResolved: data?.owners_resolved !== false,
});

// SFM scrive "Positive" | "Neutral" | "Negative": si riporta alla forma canonica anche se
// arriva con un'altra capitalizzazione, cosi' filtri e colori non la perdono.
const CANONICAL_SENTIMENTS = ["Positive", "Neutral", "Negative"];
const canonicalSentiment = (v: any): string => {
  const raw = asString(v).trim();
  const match = CANONICAL_SENTIMENTS.find(
    (s) => s.toLowerCase() === raw.toLowerCase()
  );
  return match ?? raw;
};

const transformClientSearchSentiment = (
  data: any
): ClientSearchSentiment | null => {
  // Nelle ricerche vecchie il sentiment puo' mancare o non essere un oggetto.
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const sentiment = canonicalSentiment(data.sentiment);
  // Risultato non analizzato: SFM scrive comunque confidence_subject_match=0 e
  // is_subject_match=false (EMPTY_SENTIMENT in sentiment_analysis.py), che qui
  // diventerebbero "Pertinenza 0%" e "non riguarda la persona". Senza un sentiment
  // canonico quei due valori non sono una misura: null, e la pagina mostra "—".
  const analyzed = CANONICAL_SENTIMENTS.includes(sentiment);
  return {
    sentiment,
    phrase: asString(data.phrase),
    summary: asString(data.summary),
    confidenceSubjectMatch: analyzed
      ? asNumberOrNull(data.confidence_subject_match)
      : null,
    isSubjectMatch:
      analyzed && typeof data.is_subject_match === "boolean"
        ? data.is_subject_match
        : null,
  };
};

// Ripiego per i risultati senza `domain`: l'host del link, senza "www.". Lo usa anche il
// dialog di creazione per mostrare il dominio di ogni link in partenza.
export const hostOf = (link: string): string => {
  try {
    return new URL(link).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

export const transformClientSearchResult = (data: any): ClientSearchResult => {
  const link = asString(data?.link);
  return {
    link,
    title: asString(data?.title),
    description: asString(data?.description),
    thumbnail: asString(data?.thumbnail),
    domain: asString(data?.domain) || hostOf(link),
    sentiment: transformClientSearchSentiment(data?.sentiment),
    signaled: data?.signaled === true,
  };
};

export const transformClientSearchDetail = (data: any): ClientSearchDetail => ({
  search: {
    searchId: Number(data?.search?.search_id),
    searchCreationTime: asString(data?.search?.search_creation_time),
    fullName: asString(data?.search?.full_name),
    params: uniqueStrings(data?.search?.params),
    totResults: asNumberOrNull(data?.search?.tot_results),
    status: data?.search?.status ? asString(data.search.status) : undefined,
  },
  owner: transformClientSearchOwner(data?.owner),
  results: (Array.isArray(data?.results) ? data.results : [])
    .map((d: any) => transformClientSearchResult(d))
    // Un risultato senza link non e' selezionabile ne' segnalabile.
    .filter((r: ClientSearchResult) => !!r.link),
});

export const transformClientSearchPracticeCreated = (
  data: any
): ClientSearchPracticeCreated => ({
  id: Number(data?.id),
  ownerId: asString(data?.owner_id),
  customerName: asString(data?.customer_name),
  urls: asNumberOrNull(data?.urls) ?? 0,
  skippedAlreadyInPractice: uniqueStrings(
    data?.skipped_already_in_practice,
    false
  ),
  ownerSuspended: data?.owner_suspended === true,
});

// Errore axios -> {status, code, message, urls}. Il messaggio del backend ha la
// precedenza: e' scritto per l'operatore (es. owner_suspended) e va mostrato com'e'.
export const parseClientSearchError = (
  err: any,
  fallback: string
): ClientSearchApiError => {
  const response = err?.response;
  const data = response?.data;
  const backendMessage =
    typeof data?.error === "string" ? data.error.trim() : "";
  return {
    status: typeof response?.status === "number" ? response.status : undefined,
    code: typeof data?.code === "string" ? data.code : undefined,
    message: backendMessage || fallback,
    urls: uniqueStrings(data?.urls, false),
  };
};

// Nome da mostrare per il cliente: nome e cognome, poi email, infine l'id (unico dato
// sempre presente, anche quando l'anagrafica Keycloak non e' leggibile).
export const formatClientSearchOwner = (owner?: ClientSearchOwner): string => {
  const name = [owner?.firstName, owner?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || owner?.email || owner?.id || "";
};
